/*
 * Extend Backbone.Collection to include a save function which checks each model on whether it needs to be added or updated
 * Extend Backbone.Model to include an empty validateAttribute() method as well as a delete and save method
 * Create trimStrict() function on Strings which not only trims leading and trailing whitespace, but also multiple spaces between words
 * Also implement trim function on Strings, if needed
 * Module becomes the defacto backbone implementation (extending the backbone base version)
 */

define([
  "backboneBase",
  "jquery",
  "configuration/properties",
  "messaging/messenger",
  "underscore",
  "backboneRetry",
  "i18n/i18n",
], function (BackboneModule, $, Properties, Messenger, _) {
  "use strict";

  // noinspection JSUnresolvedReference
  const BackboneBase = BackboneModule.default || BackboneModule.Backbone || BackboneModule;

  // Override jQuery.parseJSON so that an empty string is still parsed as a valid value
  const replacementParseJson = function (data) {
    if (data === null || data === void 0) {
      return {};
    }
    const stringData = data + "";
    if (stringData.trim().length === 0) {
      return {};
    }
    return JSON.parse(stringData);
  };
  if ($.parseJSON !== replacementParseJson) {
    $.parseJSON = replacementParseJson;
    if (BackboneBase.$) {
      BackboneBase.$.parseJSON = replacementParseJson;
    } else {
      BackboneBase.$ = $;
    }
  }

  const BaseCollection = BackboneBase.Collection;
  BackboneBase.Collection = BaseCollection.extend({
    //default initialize function keeps track of whether the collection has been fetched from the server
    initialize: function () {
      this.isFetched = false;
      this.isRequested = false;
      this.isSynced = false;
      this.on(
        "fetched",
        () => {
          //mark model as fetched after a 'fetched' is triggered
          this.isFetched = true;
        },
        this,
      );
      this.on(
        "request",
        () => {
          this.isRequested = true;
          this.isFetched = false;
          this.isSynced = false;
        },
        this,
      );
      this.on(
        "sync",
        () => {
          this.isSynced = true;
          this.isRequested = false;
        },
        this,
      );
      this.initializeCustom.apply(this, arguments);
    },
    initializeCustom: function () {
      //empty function for us to customize now that we took over initialize()
    },
    checkFetching: function () {
      return this.isRequested && !(this.isFetched || this.isSynced);
    },
    checkSynced: function () {
      return !this.isRequested || this.isSynced;
    },
    fetch: function (options) {
      return BaseCollection.prototype.fetch.call(this, {
        reset: true,
        ...options,
      });
    },
    /*
     * A convenience function to eliminate a common code pattern.
     * Used to call a callback function once a collection is synced.  If the collection
     * is already synced (or won't be synced because it was never requested--in the case
     * of new collections), then we call the callback function immediately.  If we haven't yet
     * synced the collection, then we wait for the sync event and then call the callback function.
     */
    onSynced: function (callback, context) {
      context = context || this;
      if (this.checkSynced()) {
        callback.apply(context);
      } else {
        this.once(
          "sync",
          () => {
            callback.apply(context);
          },
          this,
        );
      }
    },
    onSyncedPromise: function () {
      return new Promise((resolve, reject) => {
        if (this.checkSynced()) {
          resolve(this);
        } else {
          this.once("sync", () => {
            resolve(this);
          });
          this.once("error", (_model, response) => {
            reject(new Error(Messenger.getErrorFromResponse(response)));
          });
        }
      });
    },
    sync: function (method, model, options) {
      const syncOptions = options ? _.clone(options) : {};
      const success = syncOptions.success;
      const collection = this;
      syncOptions.success = (resp) => {
        collection.trigger("fetched", collection, resp, options);
        if (success) {
          success(resp);
        }
      };
      // There used to be code that overrode the options.error so that a 'parsererror' could be ignored when no
      // JSON was returned, but instead a blank string was returned.
      // The problem with this is that 'parsererror' could also be happening because a different layer is returning
      // HTML, which is an error that should not be suppressed.  And, when 'parsererror' is produced by jQuery,
      // it no longer returns the original response so there is no way of knowing why it happened.
      // A new approach taken is to override jQuery.parseJSON instead to return an empty object if an empty string
      // is passed in.
      return BackboneBase.sync.call(this, method, model, syncOptions);
    },
    //options hash variable accepts success and error functions, just like backbone's model.save function
    saveCollection: function (options) {
      options = options || {};
      const successFunction =
        options.success ||
        (() => {
          Messenger.saveAttempted(true);
        });
      const errorFunction =
        options.error ||
        ((model, response) => {
          Messenger.setErrorMsgFromResponse(response);
          Messenger.saveAttempted(false);
        });
      let errorResponse = false;
      // NOTE: Do not store this number in an object property.  Sometimes the same collection
      // can be saved multiple times in the same overall Save, and if this is an object property
      // then it can cause the count to get off and the save indicator will never go away
      let modelsToUpdate = this.length;
      //if the collection is empty, simply call the success function
      if (modelsToUpdate === 0) {
        successFunction(this);
        return;
      }
      const modelOptions = {
        saving: true,
        success: (model, response) => {
          console.log("save success");
          if (model !== undefined) {
            model.dirty = false;
          }
          modelsToUpdate--;
          if (modelsToUpdate === 0) {
            if (errorResponse) {
              //pass collection to the error function
              errorFunction(this, errorResponse);
            } else {
              //pass collection to the success function
              successFunction(this, response);
            }
          }
        },
        error: (model, response) => {
          errorResponse = response;
          modelsToUpdate--;
          if (modelsToUpdate === 0) {
            //Obviously an error occurred, no need to check the error flag
            errorFunction(this, errorResponse);
          }
        },
      };
      if (options.type) {
        modelOptions.type = options.type;
      }
      const skipNew = options.skipNew;
      this.each((model) => {
        if (model.isNew()) {
          if (skipNew) {
            modelOptions.success(model, {}, options);
            return;
          }
        } else {
          //get url for updated models
          if (model.get("self")) {
            model.url = model.get("self").href; //get own url
          }
        }
        model.saveModel(modelOptions);
      }, this);
    },
    saveCollectionPromise: function (options) {
      return new Promise((resolve, reject) => {
        const saveOptions = _.extend({}, options, {
          error: (collection, response) => {
            const error = Messenger.getErrorFromResponse(response);
            Messenger.setErrorMsgFromResponse(response);
            Messenger.saveAttempted(false);
            reject(error, collection);
          },
          success: (collection) => {
            Messenger.saveAttempted(true);
            resolve(collection);
          },
        });
        return this.saveCollection(saveOptions);
      });
    },
  });

  /**
   * A collection that is written to the server and read from the server as an entire collection,
   * never as individual models.
   */
  BackboneBase.AtomicCollection = BackboneBase.Collection.extend({
    // This is borrowed from the save() function of Backbone.Model and tweaked to instead
    // work with the models of a collection.
    save: function (options) {
      let xhr,
        models = this.models;

      const saveOptions = _.extend(
        {
          contentType: "application/json",
          saving: true,
          parse: true,
          validate: true,
        },
        options,
      );

      if (saveOptions.validate) {
        let valid = true;
        let allErrors = [];
        if (_.isFunction(this.validate)) {
          allErrors = this.validate();
          if (allErrors && allErrors.length > 0) {
            valid = false;
          }
        } else {
          this.each((model) => {
            if (model.validate) {
              const errors = model.validate(model.attributes);
              if (errors) {
                _.each(errors, (errorMessage) => {
                  allErrors.push(errorMessage);
                });
                valid = false;
              }
            }
          });
        }
        if (!valid) {
          const resp = {};
          if (allErrors.length > 0) {
            resp.responseJSON = {
              name: "Error",
              value: allErrors,
            };
          }
          if (saveOptions.error) {
            saveOptions.error(this, resp, options);
          }
          this.trigger("error", this, resp, options);
          return;
        }
      }

      // After a successful server-side save, the client is (optionally)
      // updated with the server-side state.
      if (saveOptions.data === void 0) {
        const jsonData = this.toJSON(saveOptions);
        saveOptions.data = JSON.stringify(jsonData);
      }
      const collection = this;
      let success = saveOptions.success;
      let error = saveOptions.error;
      saveOptions.success = (resp) => {
        // Ensure models are restored during synchronous saves.
        collection.models = models;
        const serverModels = collection.parse(resp, options);
        if (_.isArray(serverModels) && !collection.set(serverModels, options)) {
          return false;
        }
        if (success) {
          success(collection, resp, options);
        }
        collection.trigger("sync", collection, resp, options);
      };
      saveOptions.error = (resp) => {
        if (error) {
          error(collection, resp, options);
        }
        collection.trigger("error", collection, resp, options);
      };

      xhr = this.sync("update", this, saveOptions);

      return xhr;
    },
    saveCollection: function (options) {
      //Override to save the entire collection
      const saveOptions = _.extend(
        {
          success: () => {
            Messenger.saveAttempted(true);
          },
          error: (collection, resp) => {
            Messenger.setErrorMsgFromResponse(resp);
            Messenger.saveAttempted(false);
          },
        },
        options,
      );
      this.save(saveOptions);
    },
    saveCollectionPromise: function (options) {
      return new Promise((resolve, reject) => {
        const saveOptions = _.extend({}, options, {
          error: (collection, response) => {
            const error = Messenger.getErrorFromResponse(response);
            Messenger.setErrorMsgFromResponse(response);
            Messenger.saveAttempted(false);
            reject(error, collection);
          },
          success: (collection) => {
            Messenger.saveAttempted(true);
            resolve(collection);
          },
        });
        this.save(saveOptions);
      });
    },
  });

  function validateEmailAddress(
    value,
    { invalidEmailMessage } = {
      invalidEmailMessage: "",
    },
  ) {
    if (value) {
      //Same as the Java one except the \ is not escaped itself
      const regex = /.+@.+\..+/;
      if (!regex.test(value)) {
        return $.t(invalidEmailMessage || "commonMessages.invalidEmailAddress");
      }
    }
  }

  function validateIdCharacters(value) {
    if (value) {
      // eslint-disable-next-line no-useless-escape
      const match = /^[A-Za-z0-9\-_]*$/;
      if (!match.test(value)) {
        return $.t("commonMessages.invalidIdCharacters");
      }
    }
  }

  function validateRequiredValue(
    value,
    { emptyValueMessage, maxLength, tooLongMessage } = {
      emptyValueMessage: "",
      maxLength: -1,
      tooLongMessage: "",
    },
  ) {
    if (typeof value !== "string" || value.length === 0 || !value.trim()) {
      return $.t(emptyValueMessage || "commonMessages.emptyMessage");
    }
    if (typeof maxLength == "number" && maxLength > 0 && value.length > maxLength) {
      return $.t(tooLongMessage || "commonMessages.tooLong", {
        number: maxLength,
        current: value.length,
      });
    }
  }

  function validateSafeCharacters(value) {
    if (value) {
      const pos = value.indexOf(/[<>"\\]/);
      if (pos >= 0) {
        return $.t("commonMessages.invalidSafeCharacters");
      }
    }
  }

  function validateSimpleCharacters(value) {
    if (value) {
      // eslint-disable-next-line no-useless-escape
      const match = /^[A-Za-z0-9\-]*$/;
      if (!match.test(value)) {
        return $.t("commonMessages.invalidSimpleCharacters");
      }
    }
  }

  function validateStringLength(
    value,
    { maxLength, tooLongMessage } = {
      maxLength: -1,
      tooLongMessage: "",
    },
  ) {
    if (
      typeof value === "string" &&
      typeof maxLength == "number" &&
      maxLength > 0 &&
      value.length > maxLength
    ) {
      return $.t(tooLongMessage || "commonMessages.tooLong", {
        number: maxLength,
        current: value.length,
      });
    }
  }

  const BaseModel = BackboneBase.Model;
  BackboneBase.Model = BaseModel.extend(
    {
      defaultValidateAttributes: null,
      //default initialize function sets dirty bit as false
      //On change event, changes dirty bit to true
      //On an invalid validation attempt, will finish the save attempt as unsuccessful
      initialize: function () {
        this.dirty = false;
        this.isFetched = false;
        this.isRequested = false;
        this.isSynced = false;
        this.on(
          "change",
          () => {
            //ignore changes occurring between a fetch and sync (populating the model)
            if (this.checkSynced()) {
              this.dirty = true;
            }
          },
          this,
        );
        this.on("invalid", () => {
          Messenger.saveAttempted(false, true); //consider the save done and do not throw an error
        });
        this.on(
          "fetched",
          () => {
            //mark model as fetched after a "fetched" event is triggered
            this.isFetched = true;
          },
          this,
        );
        this.on(
          "request",
          () => {
            this.isRequested = true;
            this.isFetched = false;
            this.isSynced = false;
          },
          this,
        );
        this.on(
          "sync",
          () => {
            this.isSynced = true;
            this.isRequested = false;
          },
          this,
        );
        this.initializeCustom.apply(this, arguments);
      },
      initializeCustom: function () {
        //empty function for us to customize now that we took over initialize()
      },
      sync: function (method, model, options) {
        const syncOptions = options ? _.clone(options) : {};
        const success = syncOptions.success;
        syncOptions.success = (resp) => {
          model.trigger("fetched", model, resp, options);
          if (success) {
            success(resp);
          }
        };
        // There used to be code that overrode the options.error so that a 'parsererror' could be ignored when no
        // JSON was returned, but instead a blank string was returned.
        // The problem with this is that 'parsererror' could also be happening because a different layer is returning
        // HTML, which is an error that should not be suppressed.  And, when 'parsererror' is produced by jQuery,
        // it no longer returns the original response so there is no way of knowing why it happened.
        // A new approach taken is to override jQuery.parseJSON instead to return an empty object if an empty string
        // is passed in.
        return BackboneBase.sync.call(this, method, model, syncOptions);
      },
      validateAttribute: function (/*attribute, value, allAttributes*/) {
        //do nothing
      },
      determineValidateAttributes: function (attributes /*, options */) {
        if (!attributes || _.isEmpty(attributes)) {
          return _.result(this, "defaultValidateAttributes");
        }
        return attributes;
      },
      validate: function (attributes, options) {
        const validateAttributes = this.determineValidateAttributes(attributes, options);
        if (validateAttributes) {
          const errors = {};
          let isValid = true;
          if (_.isArray(validateAttributes)) {
            const allAttributes = _.extend({}, this.attributes);
            for (const attributeName of validateAttributes) {
              const error = this.validateAttribute(
                attributeName,
                this.get(attributeName),
                allAttributes,
              );
              if (error) {
                errors[attributeName] = error;
                isValid = false;
              }
            }
          } else {
            const allAttributes = _.extend({}, this.attributes, validateAttributes);
            for (const attributeName of Object.keys(validateAttributes)) {
              const error = this.validateAttribute(
                attributeName,
                validateAttributes[attributeName],
                allAttributes,
              );
              if (error) {
                errors[attributeName] = error;
                isValid = false;
              }
            }
          }
          if (!isValid) {
            return errors;
          }
        }
        // Allow implicit return which is undefined
      },
      validateEmailAddress: function (
        value,
        { invalidEmailMessage } = {
          invalidEmailMessage: "",
        },
      ) {
        return validateEmailAddress(value, { invalidEmailMessage });
      },
      validateIdCharacters: function (value) {
        return validateIdCharacters(value);
      },
      validateRequiredValue: function (
        value,
        { emptyValueMessage, maxLength, tooLongMessage } = {
          emptyValueMessage: "",
          maxLength: -1,
          tooLongMessage: "",
        },
      ) {
        return validateRequiredValue(value, { emptyValueMessage, maxLength, tooLongMessage });
      },
      validateSafeCharacters: function (value) {
        return validateSafeCharacters(value);
      },
      validateSimpleCharacters: function (value) {
        return validateSimpleCharacters(value);
      },
      validateStringLength: function (
        value,
        { maxLength, tooLongMessage } = {
          maxLength: -1,
          tooLongMessage: "",
        },
      ) {
        return validateStringLength(value, { maxLength, tooLongMessage });
      },
      deleteModel: function (options = {}) {
        //get url if model already exists in the db
        if (!this.isNew() && this.get("self")) {
          this.url = this.get("self").href;
        }
        const deleteOptions = _.clone(options);
        const collection = this.collection;
        const model = this;
        const success = deleteOptions.success;
        const error = deleteOptions.error;
        deleteOptions.success = (passedModel, resp) => {
          Messenger.vent.trigger("close:saving:display");
          if (collection && !model.isNew()) {
            //deletes to the server trigger a request event.  We need to manually finish it will a fetch and sync event (since
            //the model is no longer part of the collection when the events occur)
            collection.trigger("fetched");
            collection.trigger("sync");
          }
          if (_.isFunction(success)) {
            success.call(options.context || this, passedModel, resp, options);
          }
        };
        deleteOptions.error = (passedModel, xhr) => {
          Messenger.vent.trigger("close:saving:display");
          //if the model was part of a collection, add it back since it failed to delete on the server
          if (collection) {
            collection.add(model); //use old model
          }
          if (collection && !model.isNew()) {
            //deletes to the server trigger a request event.  We need to manually finish it will a fetch and sync event (since
            //the model is no longer part of the collection when the events occur)
            model.trigger("fetched");
            model.trigger("sync");
          }
          if (_.isFunction(error)) {
            error.call(options.context || this, passedModel, xhr, options);
          } else {
            Messenger.showErrorFromResponse(xhr);
          }
        };
        Messenger.vent.trigger("show:saving:display");
        //will send DELETE if model exists in the db, or will simply remove from the collection if new
        this.destroy(deleteOptions);
      },
      deleteModelPromise: function (options = {}) {
        return new Promise((resolve, reject) => {
          const deleteOptions = Object.assign(
            {
              error: (_model, response) => reject(Messenger.getErrorFromResponse(response)),
              success: (model) => resolve(model),
            },
            options,
          );
          this.deleteModel(deleteOptions);
        });
      },
      /**
       * If this is a new record, then this does nothing other than deleting all sub-models and removing it
       * from any collection it may belong to.
       * If this is an existing record, then the sub-models will also be deleted, then the record will
       * be fetched from the server again.
       * @param {*} options An optional set of options to pass to the fetch method.
       */
      discardModel: function (options) {
        const discardOptions = _.extend({}, options);
        const error = discardOptions.error;
        const success = discardOptions.success;
        this._discardSubModels();
        if (this.isNew()) {
          // Trigger a "destroy" event so that the collection will remove the model
          this.trigger("destroy", this, this.collection, options);
          if (success) {
            success(this, {}, options);
          }
          return;
        }
        Messenger.vent.trigger("show:saving:display");
        discardOptions.error = (model, resp) => {
          Messenger.vent.trigger("close:saving:display");
          if (error) {
            error(model, resp, options);
          }
        };
        discardOptions.success = (model, resp) => {
          Messenger.vent.trigger("close:saving:display");
          if (success) {
            success(model, resp, options);
          }
        };
        this.fetch(discardOptions);
      },
      /**
       * Generic function for loading models or collections that are children of the current model
       *
       * @param modelVariable Variable to which we will assign the sub model or collection to
       * @param ModelObject   A Backbone Model or Collection prototype which we create
       * @param urlExtension  Either an absolute url, a function to calculate the URL, or an url extension off the
       *                      parent object. URL extensions do not start with 'http' or the rootURL
       * @param shouldSort    Boolean that determines if we should sort a collection when synced.
       *                      Should not be used if modelObject is a Model.
       * @param doNotFetch    Boolean that overrides whether the fetch should occur
       */
      getSubModel: function (modelVariable, ModelObject, urlExtension, shouldSort, doNotFetch) {
        return this.getSubModel2(modelVariable, {
          doNotFetch: doNotFetch,
          modelClass: ModelObject,
          shouldSort: shouldSort,
          urlExtension: urlExtension,
        });
      },
      /**
       * Generic function for loading models or collections that are children of the current model
       *
       * @param modelVariable        Variable to which we will assign the sub model or collection to
       * @param options              Additional options to use to create the sub-model if needed
       * @param options.data         Data to load into the Model or Collection when creating it
       * @param options.modelClass   A Backbone Model or Collection prototype which we create
       * @param options.url          Either an absolute url or a function to calculate the URL
       * @param options.urlExtension Either an absolute url, a function to calculate the URL, or an url extension off the
       *                             parent object. URL extensions do not start with 'http' or the rootURL
       * @param options.shouldSort   Boolean that determines if we should sort a collection when synced.
       *                             Should not be used if modelObject is a Model.
       * @param options.doNotFetch   Boolean that overrides whether the fetch should occur
       */
      getSubModel2: function (modelVariable, options) {
        options = options || {};
        if (!modelVariable) {
          modelVariable = new options.modelClass(options.data);
          // Add variable to subModels array;
          if (!this.subModels) {
            this.subModels = [];
          }
          this.subModels.push(modelVariable);
          if (options.url) {
            modelVariable.url = options.url;
          } else if (options.urlExtension) {
            const urlExtension = options.urlExtension;
            if (
              typeof urlExtension == "string" &&
              !(urlExtension.startsWith("http") || urlExtension.startsWith(Properties.rootURL))
            ) {
              // url is an extension and needs the parent's URL to be appended on.
              // underscore result function works whether url is a property or function of the model.
              // a child URL is only valid if the parent is not new.
              modelVariable.url = () => {
                if (this.isNew()) {
                  throw new Error("Cannot get a child URL on a new model");
                }
                return _.result(this, "url") + urlExtension;
              };
            } else {
              modelVariable.url = urlExtension; // it's an "absolute" url or a function
            }
          }
          if (options.shouldSort) {
            modelVariable.on("sync", () => {
              modelVariable.sort();
            });
          }
          if (!this.isNew() && !options.doNotFetch && !options.data) {
            modelVariable.fetch({
              error: (model, resp) => {
                Messenger.showErrorFromResponse(resp);
              },
            });
          }
        }
        return modelVariable;
      },
      checkFetching: function () {
        return this.isRequested && !(this.isFetched || this.isSynced);
      },
      checkSynced: function () {
        return !this.isRequested || this.isSynced;
      },
      /*
       * A convenience function to eliminate a common code pattern.
       * Used to call a callback function once a model is synced.  If the model
       * is already synced (or won't be synced because it was never requested--in the case
       * of new models), then we call the callback function immediately.  If we haven't yet
       * synced the model, then we wait for the sync event and then call the callback function.
       */
      onSynced: function (callback, context) {
        context = context || this;
        if (this.checkSynced()) {
          callback.apply(context);
        } else {
          this.once("sync", () => {
            callback.apply(context);
          });
        }
      },
      onSyncedPromise: function () {
        return new Promise((resolve, reject) => {
          if (this.checkSynced()) {
            resolve(this);
          } else {
            this.once("sync", () => {
              resolve(this);
            });
            this.once("error", (_model, response) => {
              reject(new Error(Messenger.getErrorFromResponse(response)));
            });
          }
        });
      },
      /*
       * A default implementation of saving a model, with built-in
       * defaults for success and error callback functions.
       * Accepts an options hash which is passed to the Backbone.save
       * function.  Can include your own success and error functions in the options
       * hash to override the default behavior.
       */
      saveModel: function (options) {
        //define default callbacks for success or error
        const modelOptions = this._prepareSaveModelOptions(options);
        if (this._allowSave(modelOptions)) {
          //call default save function with options
          return this.save(null, modelOptions);
        }
        //fire success callback because nothing needed to be saved
        modelOptions.success(this, {}, options);
      },
      saveModelPromise: function (options) {
        return new Promise((resolve, reject) => {
          const saveOptions = _.extend({}, options, {
            error: (model, response) => {
              const error = Messenger.getErrorFromResponse(response);
              Messenger.setErrorMsgFromResponse(response);
              Messenger.saveAttempted(false);
              reject(error, model);
            },
            success: (model) => {
              Messenger.saveAttempted(true);
              resolve(model);
            },
          });
          this.saveModel(saveOptions);
        });
      },
      /**
       *  This function will try to save all the sub models of the given model.
       *  It uses an array of sub Models/Collections (modelArray) and a passed options hash (options) that can define success and error functions.
       *  Much like the save() function for Collections, it will try to save each sub Model/Collection and call the success
       *  function if all succeed and the error function if at least one fails.  Failure will not stop the function from trying
       *   to save the other Models/Collections.
       */
      saveSubModels: function (options) {
        const modelArray = this._collectSaveSubModels();
        options = options || {};
        const successFunction =
          options.success ||
          (() => {
            Messenger.saveAttempted(true);
          });
        const errorFunction =
          options.error ||
          ((model, response) => {
            Messenger.setErrorMsgFromResponse(response);
            Messenger.saveAttempted(false);
          });
        //check if there are any subModels to save
        if (!modelArray || modelArray.length === 0) {
          successFunction(this); //successful since there was nothing to do
          return;
        }
        let errorResponse = false;
        let subModelsSaved = 0;
        // NOTE: Do not store this number in an object property.  Sometimes the same object
        // can be saved multiple times in the same overall Save, and if this is an object property
        // then it can cause the count to get off and the save indicator will never go away
        const numberOfSubModels = modelArray.length;
        //define options hash for the models/collections
        const modelOptions = {
          saving: true,
          success: (model, response) => {
            subModelsSaved++;
            if (model && model.dirty) {
              //reset dirty parameter if it exists (ie. if it's a model)
              model.dirty = false;
            }
            if (subModelsSaved === numberOfSubModels) {
              if (errorResponse) {
                errorFunction(this, errorResponse, options);
              } else {
                successFunction(this, response, options);
              }
            }
          },
          error: (model, response) => {
            errorResponse = response;
            subModelsSaved++;
            if (subModelsSaved === numberOfSubModels) {
              errorFunction(this, errorResponse, options); //obviously, an error occurred, so no point in checking 'error'
            }
          },
        };
        if (options.type) {
          modelOptions.type = options.type;
        }
        //iterate through array and save each model or collection
        for (let i = 0; i < numberOfSubModels; i++) {
          if (modelArray[i].saveModel) {
            //is model
            modelArray[i].saveModel(modelOptions);
          } else {
            //is collection
            modelArray[i].saveCollection(modelOptions);
          }
        }
      },
      /**
       * Checks whether this model is allowed to be saved.  The base method always returns true if this model
       * is dirty or new.
       *
       * @param {*} options The options that were passed to the saveModel function
       * @protected
       */
      _allowSave: function (options) {
        // Dummy statement to avoid IDE warnings
        // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
        const ignored = options;
        return this.dirty || this.isNew();
      },
      /**
       * Gets the array of sub-models to save.  Default implementation just
       * returns the subModels property.
       *
       * @returns {[]} An array of sub-models.
       * @protected
       */
      _collectSaveSubModels: function () {
        return this.subModels;
      },
      /**
       * Discards the sub models for this model.  This involves clearing out the model property associated with
       * the sub model.  Default implementation only re-creates the subModels array property.
       * @protected
       */
      _discardSubModels: function () {
        this.subModels = [];
      },
      /**
       * Prepares the options being passed to the save method during the saveModel method.
       * The default implementation makes sure that a success and error callback exists which
       * then calls Messenger.saveAttempted.
       *
       * @param {*} options The options to prepare for save.
       * @returns {*} The updated options
       * @protected
       */
      _prepareSaveModelOptions: function (options) {
        const saveModelOptions = _.extend(
          {
            validate: true,
          },
          options,
          {
            saving: true,
          },
        );
        if (!saveModelOptions.url && !this.isNew()) {
          const self = this.get("self");
          if (self && self.href) {
            saveModelOptions.url = self.href;
          }
        }
        if (typeof this.saveChildren == "function" && !saveModelOptions.topLevelOnly) {
          saveModelOptions.success = this._createSaveModelSuccessWithChildren(options);
        } else {
          saveModelOptions.success = this._createSaveModelSuccessNoChildren(options);
        }
        saveModelOptions.error = this._createSaveModelError(options);
        return saveModelOptions;
      },
      _createSaveModelError: function (options) {
        if (options && options.error) {
          return options.error;
        }
        return (model, resp) => {
          Messenger.setErrorMsgFromResponse(resp);
          Messenger.saveAttempted(false);
        };
      },
      _createSaveModelSuccessNoChildren: function (options) {
        return (model, response) => {
          model.dirty = false;
          const self = model.get("self");
          if (self && self.href) {
            model.url = self.href;
          }
          if (options && options.success) {
            options.success(model, response);
          } else {
            Messenger.saveAttempted(true);
          }
        };
      },
      _createSaveModelSuccessWithChildren: function (options) {
        return (model) => {
          model.dirty = false;
          const self = model.get("self");
          if (self && self.href) {
            model.url = self.href;
          }
          this.saveChildren(options);
        };
      },
    },
    {
      validateEmailAddress: validateEmailAddress,
      validateIdCharacters: validateIdCharacters,
      validateRequiredValue: validateRequiredValue,
      validateSafeCharacters: validateSafeCharacters,
      validateSimpleCharacters: validateSimpleCharacters,
      validateStringLength: validateStringLength,
    },
  );

  BackboneBase.textComparator = function (a, b, attribute) {
    let aVal, bVal;
    if (attribute.startsWith("_thisObject.")) {
      //handle an attribute not in the backbone model
      attribute = attribute.slice("_thisObject.".length);
      aVal = a[attribute];
      bVal = b[attribute];
    } else {
      aVal = a.get(attribute);
      bVal = b.get(attribute);
    }
    if (aVal) {
      aVal = aVal.toLowerCase();
    } else {
      aVal = "";
    }

    if (bVal) {
      bVal = bVal.toLowerCase();
    } else {
      bVal = "";
    }
    let value;
    if (aVal < bVal) {
      value = -1;
    } else if (aVal > bVal) {
      value = 1;
    } else {
      //equal
      return 0;
    }
    return value;
  };

  BackboneBase.dateComparator = function (a, b, attribute) {
    let aVal, bVal;
    if (attribute.startsWith("_thisObject.")) {
      //handle an attribute not in the backbone model
      attribute = attribute.slice("_thisObject.".length);
      aVal = a[attribute];
      bVal = b[attribute];
    } else {
      aVal = a.get(attribute);
      bVal = b.get(attribute);
    }
    if (aVal === bVal) {
      return 0;
    }
    if (aVal === null || aVal === undefined) {
      aVal = 0;
    } else {
      aVal = Messenger.reqres.request("get:seconds:since:epoch", aVal);
    }
    if (bVal === null || bVal === undefined) {
      bVal = 0;
    } else {
      bVal = Messenger.reqres.request("get:seconds:since:epoch", bVal);
    }
    if (aVal < bVal) {
      return -1;
    }
    if (aVal > bVal) {
      return 1;
    }
    //equal
    return 0;
  };

  BackboneBase.numericComparator = function (a, b, attribute) {
    let aVal, bVal;
    if (attribute.startsWith("_thisObject.")) {
      //handle an attribute not in the backbone model
      attribute = attribute.slice("_thisObject.".length);
      aVal = a[attribute];
      bVal = b[attribute];
    } else {
      aVal = a.get(attribute);
      bVal = b.get(attribute);
    }
    let value;

    if (aVal < bVal) {
      value = -1;
    } else if (aVal > bVal) {
      value = 1;
    } else {
      //equal
      return 0;
    }
    return value;
  };

  //Compares two arrays of strings that are located in a model in the provided attribute, not necessarily of the same length.
  //Sorts solely based on the first item in the array, if any
  BackboneBase.arrayComparator = function (a, b, attribute) {
    let aArr, bArr;
    //get arrays to compare from the attributes hash or directly from the object
    if (attribute.startsWith("_thisObject.")) {
      //handle an attribute not in the backbone model
      attribute = attribute.slice("_thisObject.".length);
      aArr = a[attribute];
      bArr = b[attribute];
    } else {
      aArr = a.get(attribute);
      bArr = b.get(attribute);
    }
    //get the first elements to compare
    let aVal = "";
    let bVal = "";
    if (aArr.length > 0 && aArr[0]) {
      aVal = aArr[0].toLowerCase();
    }
    if (bArr.length > 0 && bArr[0]) {
      bVal = bArr[0].toLowerCase();
    }
    if (aVal < bVal) {
      return -1;
    }
    if (aVal > bVal) {
      return 1;
    }
    //equal
    return 0;
  };

  //Implementation of trim if it's not available (IE 8...)
  if (!String.prototype.trim) {
    String.prototype.trim = function () {
      //replace spaces from the start or end of the string with empty string
      return this.replace(/^\s+|\s+$/, "");
    };
  }

  String.prototype.trimStrict = function () {
    const str = this.trim();
    return str.replace(/\s\s+/g, " "); //replace sequences of two or more whitespaces with one whitespace
  };

  //implementation of startsWith
  String.prototype.startsWith = function (substring) {
    return this.slice(0, substring.length) === substring;
  };

  //implementation of endsWith
  String.prototype.endsWith = function (substring) {
    return this.slice(-substring.length) === substring;
  };

  /*
   * This function is used to parse the HTML from a response into a read-only version to be shown on the screen
   * in the response preview.  Right now, it makes sure that angled brackets are all unencoded.  It seems like
   * the old HTML editor liked to occasionally write &lt; and &gt; for < and > for some reason.  It also removes any scripts
   * from the response.
   */
  String.prototype.parseHtml = function () {
    //reg ex matches html elements in <>
    let newHtml = this.replace(/&lt;/gm, "<");
    newHtml = newHtml.replace(/&gt;/gm, ">");
    newHtml = newHtml.replace(/<script.*?<\/script>/gm, ""); //replace all scripts with a non-greedy regular expression
    newHtml = newHtml.replace(/&amp;nbsp;/gm, " "); //replace &nbsp; with a space
    return newHtml;
  };

  String.prototype.escapeHtml = function () {
    return _.escape(this);
  };

  /**
   * Returns the number of days back to go by default for reports, based on the
   * current day.
   * @returns {Number} The number of days back
   */
  Date.prototype.getDefaultDaysBack = function () {
    let daysBack = 30;
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    const dayOfMonth = today.getDate();
    if (month === 0 || month === 7) {
      //January or August - 31 day month after 31 day month
      daysBack = 31;
    } else if (month === 4 || month === 6 || month === 9 || month === 11) {
      //May, July, October, December - 31 day month after 30 day month
      //If it's July 27 12:00 AM, go back 30 days till June 27
      //If it's July 31 12:00 AM, go back 30 days till July 1 12:00 AM (12 AM Jul 1 - 11:59 PM Jul 30)
      daysBack = 30;
    } else if (month === 3 || month === 5 || month === 8 || month === 10 || month === 1) {
      //April, June, September, November, February - 28 - 30 day month after 31-day month
      daysBack = 31;
    } else if (month === 2) {
      //March
      let isLeapYear = false;
      if (year % 400 === 0) {
        isLeapYear = true;
      } else if (year % 100 === 0) {
        isLeapYear = false;
      } else if (year % 4 === 0) {
        isLeapYear = true;
      }
      if (isLeapYear) {
        daysBack = 29;
        //On March 30th, still go 12:00 AM March 1st - 11:59 PM March 29th (12:00 March 30th)
        //On March 31st, still start at 12:00 March 1st.  Go back more.
        if (dayOfMonth > 30) {
          daysBack += dayOfMonth - 30;
        }
      } else {
        daysBack = 28;
        //On March 29th, still go 12:00 AM March 1st - 11:59 PM March 28th (12:00 March 29th)
        //On March 30/31st, still start at 12:00 March 1st.  Go back more.
        if (dayOfMonth > 29) {
          daysBack += dayOfMonth - 29;
        }
      }
    }
    return daysBack;
  };

  return BackboneBase;
});
